import React from 'react';
import { Button } from 'components/index';
import { FormControlLabel, Stack, Switch, Tooltip, Typography } from '@mui/material';
import { usePayappProcess } from '../../context/PayappProcessContext';
import { HEIGHTS } from '../report/constants';
import Footer from '../general/Footer';
import {
  finalFieldsHeaders,
  validateRows,
  mergePayappIntoProjectDraw,
  autoMergeFuzzyMatches,
  detectSkippedPayapp,
} from '../file-upload/Payapp.util';
import { usePrompt } from '../AppPrompt';
import { toast } from 'react-toastify';
import { payappProcessSteps } from '../../pages/PayappProcess';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../common/constants/urls';
import {
  PayappHeaderMappingDTO,
  DrawLineItemNoteDTO,
  DrawLineItemNoteContentDTO,
  ProjectLineItemSubtotalDTO,
  ProjectLineItemsAndSummary,
  ProjectPreviousFundingDTO,
} from '../project/project.type';
import { getNextAdjustmentApplicationNumber, PERIOD_CHANGE_BOLD_FIELDS } from '../project/project.util';
import { unescapeCSS, parseCurrency, formatCurrency, differsWithBuffer, parsePeriodDateToISO } from '../../common/utils/common.util';
import { updatePayappHeaderMappings } from '../project/project.api';
import FeedbackButton from '../general/feedback/FeedbackButton';
import { useMaterialUIController, setMiniSidenav } from 'context/index';
import { savePayappDraft } from './PayappDraft.api';
import { backendToLabelMap } from '../file-upload/Payapp.util';
import { getProjectFiles, addProjectHistory } from '../project/project.api';
import { getPayappDraft } from './PayappDraft.api';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { Analytics, ANALYTICS_EVENTS } from '../../services/analyticsService';

interface PayappProcessFooterProps {
  getNoteContent: () => string;
  /** Called when user wants to start the second payapp upload on step 4 */
  onUploadSecondPayapp?: () => void;
  /** Called after step changes so the URL can be updated (e.g. for Hotjar). */
  onStepUrlChange?: (stepIndex: number) => void;
}

const PayappProcessFooter: React.FC<PayappProcessFooterProps> = ({ getNoteContent, onUploadSecondPayapp, onStepUrlChange }) => {
  const { state, setActiveStep, setFinalizedParsedRows, setProjectDraw, updateState, pendingMergeEventsRef, payappSessionIdRef } =
    usePayappProcess();
  const { getConfirmation } = usePrompt();
  const navigate = useNavigate();
  const [controller, dispatch] = useMaterialUIController();
  const [isSavingDraft, setIsSavingDraft] = React.useState(false);

  // Stable project label derived from context — used in all analytics events.
  const projectLabel = `${state.projectNumber ?? ''} - ${state.projectName ?? ''}`.trim();

  // Capture mount-time values in a ref so the open/start effect has no deps.
  const mountAnalyticsRef = React.useRef({
    projectId: String(state.projectId ?? ''),
    projectLabel,
    drawName: state.projectDraw?.projectDrawSummary?.name ?? '',
    sessionId: payappSessionIdRef.current,
  });

  // Fire payapp_open once when the workflow mounts and start the task timer.
  React.useEffect(() => {
    const { projectId, projectLabel: label, drawName, sessionId } = mountAnalyticsRef.current;
    Analytics.track(ANALYTICS_EVENTS.PAYAPP_OPEN, {
      project_id: projectId,
      project_label: label,
      draw_name: drawName,
      payapp_session_id: sessionId,
    });
    Analytics.timeEvent(ANALYTICS_EVENTS.TASK_COMPLETED);
    Analytics.track(ANALYTICS_EVENTS.TASK_STARTED, {
      task_type: 'payapp',
      project_id: projectId,
      project_label: label,
      draw_name: drawName,
      payapp_session_id: sessionId,
    });
  }, []);

  // Monitored columns for discrepancies
  const monitoredColumns = ['originalBudget', 'reallocation', 'changeOrder', 'revisedBudget'];
  const payappOnlyMonitoredColumns = ['previousApplication', 'completedStoredToDate', 'balanceToComplete'];

  /**
   * Computes system values for a line item in the dual-payapp workflow.
   *
   * With payapp-1 final draw as the project base:
   *  - item.originalBudget     = payapp-1 OB (from merged project)
   *  - item.reallocation       = payapp-2 reallocation (set by standard merge)
   *  - item.changeOrder        = payapp-2 changeOrder (set by standard merge)
   *  - item.previousApplication = payapp-1 PA (fixed in step 2→3 post-processing)
   *  - item.completeThisPeriod  = CT1 + CT2 (fixed in step 2→3 post-processing)
   *  - item.storedThisPeriod    = ST1 + ST2 (fixed in step 2→3 post-processing)
   *
   * payapp-2 raw values (payappRow) are used for completedStoredToDate / balanceToComplete / retainage.
   */
  const buildDualPayappSystemValues = (item: any) => {
    const p2 = item.payappRow || {};
    const toNum = (v: any) => (v != null ? parseCurrency(String(v)) : undefined);

    const originalBudget = item.originalBudget ?? 0;
    const reallocation = item.reallocation ?? 0;
    const changeOrder = item.changeOrder ?? 0;
    const revisedBudget = originalBudget + reallocation + changeOrder;

    const previousApplication = item.previousApplication ?? 0;
    const completeThisPeriod = item.completeThisPeriod ?? 0;
    const storedThisPeriod = item.storedThisPeriod ?? 0;

    const completedStoredToDate = toNum(p2.completedStoredToDate) ?? previousApplication + completeThisPeriod + storedThisPeriod;
    const balanceToComplete = toNum(p2.balanceToComplete) ?? revisedBudget - completedStoredToDate;
    const retainage = toNum(p2.retainage) ?? item.retainage ?? 0;

    return {
      originalBudget,
      reallocation,
      changeOrder,
      revisedBudget,
      previousApplication,
      completeThisPeriod,
      storedThisPeriod,
      completedStoredToDate,
      balanceToComplete,
      retainage,
    };
  };

  // Updated function
  const createDiscrepancyNotes = (payappValues: any, projectValues: any, systemValues: any): DrawLineItemNoteDTO | undefined => {
    const noteContent: DrawLineItemNoteContentDTO[] = [];

    [...monitoredColumns, ...payappOnlyMonitoredColumns].forEach(field => {
      const rawPayappValue = payappValues[field] === '' ? undefined : payappValues[field];
      const rawProjectValue = projectValues[field] === '' ? undefined : projectValues[field];
      const rawSystemValue = systemValues[field] === '' ? undefined : systemValues[field];

      const payappValue = rawPayappValue != null ? +parseCurrency(rawPayappValue).toFixed(2) : rawPayappValue;
      const projectValue = rawProjectValue != null ? +parseCurrency(rawProjectValue).toFixed(2) : rawProjectValue;
      let systemValue = rawSystemValue != null ? +parseCurrency(rawSystemValue).toFixed(2) : rawSystemValue;
      if (systemValue) {
        systemValue = +systemValue.toFixed(2);
      }

      if (payappOnlyMonitoredColumns.includes(field)) {
        if (payappValue !== undefined && systemValue !== undefined && Math.abs(payappValue - systemValue) >= 1.0) {
          noteContent.push({
            field,
            payappValue,
            projectValue,
          });
        }
      } else if (
        payappValue !== undefined &&
        projectValue !== undefined &&
        (Math.abs(payappValue - projectValue) >= 1.0 ||
          Math.abs(payappValue - (systemValue ?? payappValue)) >= 1.0 ||
          Math.abs(projectValue - (systemValue ?? projectValue)) >= 1.0)
      ) {
        noteContent.push({
          field,
          payappValue,
          projectValue,
        });
      }
    });

    if (noteContent.length === 0) return undefined;

    return {
      noteContent,
    };
  };

  /**
   * ProjectTable and the export bold Reallocation / Change Order cells whose current value differs from last month's (the
   * note's projectValue). Last month is state.originalProjectDraw — in the dual flow projectRow is payapp-1's result, so
   * existing notes get projectValue set to last month's value. For any other row whose value changed, a bolding-only entry
   * is added with payappValue === projectValue so it doesn't show as a payapp discrepancy.
   */
  const withLastPeriodChangeNotes = (item: any, note: DrawLineItemNoteDTO | undefined): DrawLineItemNoteDTO | undefined => {
    const lastPeriodItem = state.originalProjectDraw?.lineItems?.find((original: any) => original.id === item.id);
    if (!lastPeriodItem || item.isDeleted) return note;

    const noteContent = [...(note?.noteContent || [])];
    PERIOD_CHANGE_BOLD_FIELDS.forEach(field => {
      const currentValue = +parseCurrency(String(item[field] ?? 0)).toFixed(2);
      const lastPeriodValue = +parseCurrency(String(lastPeriodItem[field] ?? 0)).toFixed(2);
      const existingIndex = noteContent.findIndex(content => content.field === field);
      if (existingIndex >= 0) {
        noteContent[existingIndex] = { ...noteContent[existingIndex], projectValue: lastPeriodValue };
        return;
      }
      if (Math.abs(currentValue - lastPeriodValue) < 1.0) return;
      noteContent.push({ field, payappValue: lastPeriodValue, projectValue: lastPeriodValue });
    });

    return noteContent.length > 0 ? { noteContent } : undefined;
  };

  const onSecondStepNext = async () => {
    if (state.tableRows.length === 0) {
      toast.error('No line items to Add!');
      return false;
    }

    const rowsToSave =
      state.selectedRowIds.length > 0 ? state.tableRows.filter(row => state.selectedRowIds.includes(row.id)) : state.tableRows;

    const assignedHeaders = Object.values(state.fieldHeadersMapping);
    const isOriginalBudgetAssigned = assignedHeaders.includes('Original Budget');
    const isRevisedBudgetAssigned = assignedHeaders.includes('Revised Budget');

    const missingField =
      !lineItems.length || allLineItemsArePayappOnly
        ? !isOriginalBudgetAssigned
          ? 'Original Budget'
          : null
        : !isRevisedBudgetAssigned
          ? 'Revised Budget'
          : null;

    if (missingField) {
      await getConfirmation({
        heading: `${missingField} Required`,
        description:
          `The <b>${missingField}</b> column must be assigned before continuing. ` +
          `Please go back and map a column to <i>${missingField}</i>.`,
        confirmButton: { label: 'Go Back', color: 'primary' },
      });
      return false;
    }

    const missingLineItemFields = finalFieldsHeaders.filter(
      option => !Object.values(state.fieldHeadersMapping).includes(option.headerName),
    );

    if (!validateRows(rowsToSave, state.fieldHeadersMapping)) return false;

    if (missingLineItemFields.length > 0) {
      const isConfirmed = await getConfirmation({
        heading: 'Missing Columns!',
        description: `These columns are missing in the contractor's payapp. <br/>
          ${missingLineItemFields.map(option => `<i>${option.headerName}</i>`).join('<br/>')} <br/> 
          <br/>
          You can go back and assign these columns to the Payapp fields.
          If you continue, default values will be populated. <br/>
          <br/>
          Do you wish to proceed?`,
        confirmButton: {
          label: 'Continue',
          color: 'error',
        },
        cancelButton: {
          label: 'Cancel',
          color: 'secondary',
        },
      });

      if (!isConfirmed) return false;
    }

    return true;
  };

  const isFieldHeaderMapped = (key: string) =>
    state.fieldHeadersMapping[key] && finalFieldsHeaders.some(lineItemField => lineItemField.headerName === state.fieldHeadersMapping[key]);

  const onFinalStepConfirm = async () => {
    const periodBeginningRaw = state.coverPageValues['Period Beginning'];
    const periodEndingRaw = state.coverPageValues['Period Ending'];
    const invalidDateField =
      (periodBeginningRaw && !parsePeriodDateToISO(String(periodBeginningRaw)) && 'Period Beginning') ||
      (periodEndingRaw && !parsePeriodDateToISO(String(periodEndingRaw)) && 'Period Ending');

    if (invalidDateField) {
      toast.error(`${invalidDateField} is not a recognized date format. Please re-enter it using the date picker before continuing.`);
      return;
    }

    if (state.isManualUpdate) {
      const updatedDraw = { ...state.projectDraw };
      updatedDraw.lineItems = updatedDraw.lineItems.map((item: any) => {
        const cdccApproved = (item.completeThisPeriod || 0) + (item.storedThisPeriod || 0);
        const revisedBudget = (item.originalBudget || 0) + (item.reallocation || 0) + (item.changeOrder || 0);
        const completedStoredToDate = (item.previousApplication || 0) + cdccApproved;
        const balanceToComplete = revisedBudget - completedStoredToDate;
        const systemValues = {
          originalBudget: item.originalBudget,
          reallocation: item.reallocation,
          changeOrder: item.changeOrder,
          revisedBudget,
          previousApplication: item.previousApplication,
          completedStoredToDate,
          balanceToComplete,
        };
        let discrepancyNotes: ReturnType<typeof createDiscrepancyNotes> = undefined;
        try {
          discrepancyNotes =
            item.payappRow && item.projectRow ? createDiscrepancyNotes(item.payappRow, item.projectRow, systemValues) : undefined;
        } catch {
          // ignore — discrepancy notes are non-critical; proceed without them
        }
        return {
          ...item,
          cdccApproved,
          completedStoredToDate,
          balanceToComplete,
          note: withLastPeriodChangeNotes(item, discrepancyNotes),
          isEdited: !item.isCreated,
        };
      });

      // Recalculate budget summary from the updated line items so all derived fields
      // (totalCompletedAndStored, totalEarnedLessRetainage, totalAmountReccForFunding, etc.)
      // reflect the values the user entered — matching the same formula used in ProjectDetails.
      if (updatedDraw.budgetSummary) {
        const activeItems = updatedDraw.lineItems.filter((li: any) => !li.isDeleted);
        const originalContractBudgetSum = activeItems.reduce((s: number, li: any) => s + (li.originalBudget || 0), 0);
        const reallocsChangeOrders = activeItems.reduce((s: number, li: any) => s + (li.reallocation || 0) + (li.changeOrder || 0), 0);
        const contractBudgetSumToDate = originalContractBudgetSum + reallocsChangeOrders;
        const totalCompletedAndStored = activeItems.reduce(
          (s: number, li: any) => s + (li.previousApplication || 0) + (li.cdccApproved || 0),
          0,
        );
        const totalRetainageHeld = -activeItems.reduce((s: number, li: any) => s + (li.retainage || 0), 0);
        const totalRetainageHeldPercentage = totalCompletedAndStored > 0 ? (-totalRetainageHeld / totalCompletedAndStored) * 100 : 0;
        const totalEarnedLessRetainage = totalCompletedAndStored + totalRetainageHeld;
        const lessPreviousFundings =
          updatedDraw.budgetSummary.updatedLessPreviousFunding ?? updatedDraw.budgetSummary.lessPreviousFundings ?? 0;
        const currentPaymentDue = totalEarnedLessRetainage - lessPreviousFundings;
        const balanceToCompletePlusRetainage = contractBudgetSumToDate - totalCompletedAndStored - totalRetainageHeld;
        const adjustmentsByCDCC = updatedDraw.budgetSummary.adjustmentsByCDCC ?? 0;
        const totalAmountReccForFunding = adjustmentsByCDCC + currentPaymentDue;
        const requestedAmount =
          updatedDraw.budgetSummary.updatedRequestedAmount !== undefined
            ? parseCurrency(updatedDraw.budgetSummary.updatedRequestedAmount.toString())
            : (updatedDraw.budgetSummary.requestedAmount ?? 0);
        const difference = totalAmountReccForFunding - requestedAmount;
        const totalContractBudgetPerComplete =
          contractBudgetSumToDate === 0 ? 0 : (totalCompletedAndStored / contractBudgetSumToDate) * 100;

        updatedDraw.budgetSummary = {
          ...updatedDraw.budgetSummary,
          originalContractBudgetSum,
          reallocationsChangeOrders: reallocsChangeOrders,
          contractBudgetSumToDate,
          totalCompletedAndStored,
          totalRetainageHeld,
          totalRetainageHeldPercentage,
          totalEarnedLessRetainage,
          lessPreviousFundings,
          currentPaymentDue,
          balanceToCompletePlusRetainage,
          totalAmountReccForFunding,
          requestedAmount,
          difference,
          totalContractBudgetPerComplete,
          isEdited: true,
        };
      }

      if (updatedDraw.projectDrawSummary) {
        updatedDraw.projectDrawSummary = {
          ...updatedDraw.projectDrawSummary,
          applicationNumber: state.coverPageValues['Application Number'],
          ...(state.coverPageValues['Period Beginning'] && {
            periodBeginning: parsePeriodDateToISO(String(state.coverPageValues['Period Beginning'])),
          }),
          ...(state.coverPageValues['Period Ending'] && {
            periodEnding: parsePeriodDateToISO(String(state.coverPageValues['Period Ending'])),
          }),
          isEdited: true,
        };
      }

      const manualLPF = parseCurrency(
        (updatedDraw.budgetSummary?.updatedLessPreviousFunding ?? updatedDraw.budgetSummary?.lessPreviousFundings ?? '0').toString(),
      );
      const expectedLPF = computeOriginalLPF();
      if (differsWithBuffer(manualLPF, expectedLPF, 0.01)) {
        const existingFundings = updatedDraw.projectDrawSummary?.previousFundings ?? [];
        updatedDraw.projectDrawSummary = {
          ...updatedDraw.projectDrawSummary,
          previousFundings: [
            ...existingFundings,
            buildLPFAdjustmentFunding(existingFundings, Number((manualLPF - expectedLPF).toFixed(2))),
          ],
        };
        if (updatedDraw.budgetSummary) {
          const { updatedLessPreviousFunding: _omit, ...budgetSummaryWithoutEdit } = updatedDraw.budgetSummary;
          updatedDraw.budgetSummary = budgetSummaryWithoutEdit;
        }
      }

      const noteContent = getNoteContent();
      const payappMergedDrawWithNotes = {
        ...updatedDraw,
        notes: noteContent ? [{ isPayappNote: true, content: `${state.projectDraw.projectDrawSummary.name}\n${noteContent}` }] : [],
      };
      const drawName = state.projectDraw?.projectDrawSummary?.name ?? '';
      const sessionId = payappSessionIdRef.current;
      Analytics.track(ANALYTICS_EVENTS.PAYAPP_CONFIRM, {
        project_id: String(state.projectId ?? ''),
        project_label: projectLabel,
        draw_name: drawName,
        payapp_session_id: sessionId,
      });
      Analytics.track(ANALYTICS_EVENTS.TASK_COMPLETED, {
        task_type: 'manual_payapp',
        project_id: String(state.projectId ?? ''),
        project_label: projectLabel,
        draw_name: drawName,
        payapp_session_id: sessionId,
      });
      navigate(`${ROUTES.PROJECTS}/${state.projectId}`, {
        state: { payappDrawId: state.payappDrawId, payappMergedDraw: payappMergedDrawWithNotes },
      });
      return;
    }

    try {
      const finalizedHeaderMappings: PayappHeaderMappingDTO[] = Object.keys(state.fieldHeadersMapping)
        .map(key => {
          const unescapedKey = unescapeCSS(key);
          const header = state.fieldHeadersMapping[key];
          const headerKey =
            header === 'Unassigned' ? null : finalFieldsHeaders.find(option => option.headerName === header)?.field || header;
          return { sourceHeader: unescapedKey, targetHeader: headerKey };
        })
        .filter(
          mapping =>
            mapping.targetHeader !== null &&
            mapping.sourceHeader !== mapping.targetHeader &&
            (mapping.targetHeader !== null || mapping.sourceHeader !== null),
        );
      await updatePayappHeaderMappings(state.projectId, finalizedHeaderMappings);
    } catch (error) {
      console.error('Error saving headers mapping:', error);
    }

    // Create updated project draw with discrepancy notes
    const updatedDraw = { ...state.projectDraw };

    // In second-payapp flow, build a lookup of flags from payapp-1 so we can
    // impose the same isCreated / isUpdated / isDeleted semantics on any
    // carry-over line item in payapp-2 (items that existed by end of payapp-1).
    const payapp1FlagsById: Record<string, { isCreated?: boolean; isUpdated?: boolean; isDeleted?: boolean }> = {};
    if (state.isSecondPayappFlow && state.payapp1FinalDraw) {
      (state.payapp1FinalDraw.lineItems || []).forEach((p1: any) => {
        if (!p1.id) return;
        payapp1FlagsById[p1.id] = {
          isCreated: p1.isCreated,
          isUpdated: p1.isUpdated,
          isDeleted: p1.isDeleted,
        };
      });
    }

    // set lesspreviousfundings to userinput value from the compare step
    if (updatedDraw.budgetSummary) {
      // In the dual-payapp workflow the displayed Requested Amount is P1+P2 combined,
      // so we must write that combined value into the budget summary before saving.
      let requestedAmountOverride: number | undefined;
      let differenceOverride: number | undefined;
      if (state.isSecondPayappFlow && state.firstPayappSnapshot) {
        const p1Requested = parseCurrency(state.firstPayappSnapshot.coverPageValues['Requested Amount']?.toString() ?? '0');
        const p2Requested = parseCurrency(state.coverPageValues['Requested Amount']?.toString() ?? '0');
        requestedAmountOverride = p1Requested + p2Requested;
        // Recalculate Difference so it stays consistent with the combined Requested Amount.
        const totalForFunding = updatedDraw.budgetSummary.totalAmountReccForFunding ?? 0;
        differenceOverride = totalForFunding - requestedAmountOverride;
      }

      updatedDraw.budgetSummary = {
        ...updatedDraw.budgetSummary,
        // Update lessPreviousFundings with the user input value from the compare step
        ...(updatedDraw.budgetSummary.updatedLessPreviousFunding !== undefined && {
          lessPreviousFundings: updatedDraw.budgetSummary.updatedLessPreviousFunding,
        }),
        ...(requestedAmountOverride !== undefined && { requestedAmount: requestedAmountOverride }),
        ...(differenceOverride !== undefined && { difference: differenceOverride }),
        // Mark as edited so it gets included in save operations
        isEdited: true,
      };
    }
    if (updatedDraw.projectDrawSummary) {
      updatedDraw.projectDrawSummary = {
        ...updatedDraw.projectDrawSummary,
        applicationNumber: state.coverPageValues['Application Number'],
        ...(state.coverPageValues['Period Beginning'] && {
          periodBeginning: parsePeriodDateToISO(String(state.coverPageValues['Period Beginning'])),
        }),
        ...(state.coverPageValues['Period Ending'] && {
          periodEnding: parsePeriodDateToISO(String(state.coverPageValues['Period Ending'])),
        }),
        isEdited: true,
      };
    }

    // The user's manual edit to "Less Previous Fundings" on the compare step (step 4) lives at
    // budgetSummary.updatedLessPreviousFunding — the editable field — not
    // coverPageValues['Less Previous Fundings'] (the read-only PDF-extracted value). It must drive
    // the skip row's amountFunded below so a later manual edit is reflected, not discarded.
    const editedLPF = parseCurrency(
      (updatedDraw.budgetSummary?.updatedLessPreviousFunding ?? updatedDraw.budgetSummary?.lessPreviousFundings ?? '0').toString(),
    );

    // When the user opted to handle a skipped payapp, append a "Not inspected by CDCC" funding row.
    // amountFunded is based on the user's final edited LPF so a manual edit to the box carries
    // through into the skip row instead of being silently discarded.
    if (state.handleSkippedPayapp && state.skippedPayappRange) {
      const { start, end } = state.skippedPayappRange;
      const appNumStr = start === end ? `${start}*` : `${start}-${end}*`;
      const amountFunded = editedLPF - computeOriginalLPF();
      const existingFundings = updatedDraw.projectDrawSummary?.previousFundings ?? [];
      updatedDraw.projectDrawSummary = {
        ...updatedDraw.projectDrawSummary,
        previousFundings: [
          ...existingFundings,
          {
            id: `temp-${Date.now()}`,
            applicationNumber: appNumStr,
            applicationDate: undefined,
            amountFunded,
            receivedByContractor: amountFunded,
            approvedAmount: amountFunded,
            retainage: 0,
            percentRequested: 0,
            percentComplete: 0,
            totalContractAmount: 0,
            displayOrder: existingFundings.length + 1,
            comment: undefined,
            reviewedByCDCC: false,
          },
        ],
      };
    }

    const fundingsBeforeAdjustment = updatedDraw.projectDrawSummary?.previousFundings ?? [];
    // Compare the edit against the payapp's originally-stated LPF — the same baseline the Step 4
    // "Difference" comparison next to the box shows the user — so a leftover adjustment row is
    // added whenever that on-screen Difference is non-zero (skip flow or not).
    const expectedReceivedByContractorTotal = parseCurrency((state.coverPageValues['Less Previous Fundings'] ?? '0').toString());

    if (differsWithBuffer(editedLPF, expectedReceivedByContractorTotal, 0.01)) {
      const receivedByContractorDifference = Number((expectedReceivedByContractorTotal - editedLPF).toFixed(2));
      updatedDraw.projectDrawSummary = {
        ...updatedDraw.projectDrawSummary,
        previousFundings: [
          ...fundingsBeforeAdjustment,
          buildLPFAdjustmentFunding(fundingsBeforeAdjustment, receivedByContractorDifference),
        ],
      };
    }

    updatedDraw.lineItems = updatedDraw.lineItems.map((item: any) => {
      // Clear any existing notes first
      item.note = undefined;

      if (state.isSecondPayappFlow) {
        // --- Dual-payapp workflow: apply combined column rules ---
        const sv = buildDualPayappSystemValues(item);

        // Patch the item fields with the dual-payapp computed values so the
        // downstream report and discrepancy notes are based on the merged data.
        const payapp1Flags = payapp1FlagsById[item.id];

        // If this line existed at the end of payapp-1, use its flags as the
        // baseline for isCreated / isUpdated, but NEVER override isDeleted from
        // payapp-2. Deletions performed in the second workflow (step 3/4) must
        // be honored in the final result. Also, if a line is deleted, it should
        // not be treated as created/updated.
        const patchedItem = {
          ...item,
          originalBudget: sv.originalBudget,
          reallocation: sv.reallocation,
          changeOrder: sv.changeOrder,
          previousApplication: sv.previousApplication,
          completeThisPeriod: sv.completeThisPeriod,
          storedThisPeriod: sv.storedThisPeriod,
          retainage: sv.retainage,
          cdccApproved: sv.completeThisPeriod + sv.storedThisPeriod,
          ...(payapp1Flags && {
            isCreated: payapp1Flags.isCreated,
            isUpdated: payapp1Flags.isUpdated,
          }),
          ...(item.isDeleted && {
            isCreated: false,
            isUpdated: false,
          }),
        };

        // Discrepancy notes compare payapp-2 values vs project values using the dual systemValues
        const discrepancyNotes =
          item.payappRow || item.projectRow ? createDiscrepancyNotes(item.payappRow || {}, item.projectRow || {}, sv) : undefined;

        return {
          ...patchedItem,
          note: withLastPeriodChangeNotes(patchedItem, discrepancyNotes),
        };
      }

      // --- Single-payapp workflow (original logic) ---
      const calculatedCdccApproved = (item.completeThisPeriod || 0) + (item.storedThisPeriod || 0);
      const shouldRecalculateCdccApproved = (!item.cdccApproved || item.cdccApproved === 0) && calculatedCdccApproved !== 0;

      if (!item.payappRow || !item.projectRow) {
        return {
          ...item,
          note: withLastPeriodChangeNotes(item, undefined),
          ...(shouldRecalculateCdccApproved && { cdccApproved: calculatedCdccApproved }),
        };
      }

      const effectiveCdccApproved = shouldRecalculateCdccApproved ? calculatedCdccApproved : item.cdccApproved || 0;
      const systemValues = {
        originalBudget: item.originalBudget,
        reallocation: item.reallocation,
        changeOrder: item.changeOrder,
        revisedBudget: (item.originalBudget || 0) + (item.reallocation || 0) + (item.changeOrder || 0),
        previousApplication: item.previousApplication,
        completedStoredToDate: (item.previousApplication || 0) + effectiveCdccApproved,
        balanceToComplete:
          (item.originalBudget || 0) +
          (item.reallocation || 0) +
          (item.changeOrder || 0) -
          ((item.previousApplication || 0) + effectiveCdccApproved),
      };

      const discrepancyNotes = createDiscrepancyNotes(item.payappRow, item.projectRow, systemValues);

      return {
        ...item,
        note: withLastPeriodChangeNotes(item, discrepancyNotes),
        ...(shouldRecalculateCdccApproved && { cdccApproved: calculatedCdccApproved }),
      };
    });
    const noteContent = getNoteContent();
    const payappMergedDrawWithNotes = {
      ...updatedDraw,
      notes: noteContent
        ? [
            {
              isPayappNote: true,
              content: `${state.projectDraw.projectDrawSummary.name}\n${noteContent}`,
            },
          ]
        : [],
    };
    const drawName = state.projectDraw?.projectDrawSummary?.name ?? '';
    const sessionId = payappSessionIdRef.current;
    Analytics.track(ANALYTICS_EVENTS.PAYAPP_CONFIRM, {
      project_id: String(state.projectId ?? ''),
      project_label: projectLabel,
      draw_name: drawName,
      payapp_session_id: sessionId,
    });
    Analytics.track(ANALYTICS_EVENTS.TASK_COMPLETED, {
      task_type: 'payapp',
      project_id: String(state.projectId ?? ''),
      project_label: projectLabel,
      draw_name: drawName,
      payapp_session_id: sessionId,
    });

    // Flush all accumulated merge events now that the user has confirmed
    pendingMergeEventsRef.current.forEach(({ eventName, properties }) => {
      Analytics.track(eventName, properties as any);
    });
    pendingMergeEventsRef.current = [];

    navigate(`${ROUTES.PROJECTS}/${state.projectId}`, {
      state: { payappDrawId: state.payappDrawId, payappMergedDraw: payappMergedDrawWithNotes },
    });
  };

  const isFinalStep = state.activeStep === payappProcessSteps.length - 1;
  const mergeStepIndex = 2; // Step 3 "Merge Into Last Month" (0-based)

  // Show the "Upload second payapp" button only on the final step of the FIRST payapp
  // (i.e. not when we're already processing payapp-2)
  const showUploadSecondPayapp =
    isFinalStep && !state.isSecondPayappFlow && !state.firstPayappSnapshot && !!onUploadSecondPayapp && !state.isManualUpdate;

  // Step 3 warnings: reallocation mismatch and unmerged line items (same logic as banner on page)
  const lineItems = state.projectDraw?.lineItems?.filter((item: any) => !item.isDeleted) || [];

  const hasReallocationDiscrepancy = (item: any): boolean => {
    if (!item.payappRow && !item.projectRow) return false;
    const sys = Number(item.reallocation ?? 0);
    const payapp = item.payappRow?.reallocation;
    const project = item.projectRow?.reallocation;

    if (payapp !== undefined && differsWithBuffer(sys, payapp)) return true;
    if (project !== undefined && differsWithBuffer(sys, project)) return true;

    return false;
  };

  // Only consider reallocations on rows whose Reallocation cell is currently a discrepancy (red box)
  const discrepantReallocationItems = lineItems.filter((item: any) => hasReallocationDiscrepancy(item));
  const unexpectedReallocationTotal = discrepantReallocationItems.reduce(
    (sum: number, item: any) => sum + (item.reallocation || 0) + (item.changeOrder || 0),
    0,
  );
  const reallocationDifference = Math.abs(unexpectedReallocationTotal);
  const hasReallocationMismatch = discrepantReallocationItems.length > 0;
  const payappOnlyCount = lineItems.filter((item: any) => item.isCreated).length;
  const projectOnlyCount = lineItems.filter((item: any) => item.exitingAndNotMatched).length;
  const hasUnmatchedLineItems = payappOnlyCount > 0 || projectOnlyCount > 0;
  const allLineItemsArePayappOnly = lineItems.length > 0 && payappOnlyCount === lineItems.length;
  const unmatchedLineItemsDescription = (() => {
    if (!hasUnmatchedLineItems) return '';

    const parts: string[] = [];

    if (payappOnlyCount > 0) {
      parts.push(`${payappOnlyCount} payapp row${payappOnlyCount !== 1 ? 's' : ''}`);
    }

    if (projectOnlyCount > 0) {
      parts.push(`${projectOnlyCount} row${projectOnlyCount !== 1 ? 's' : ''} from last month`);
    }

    if (!parts.length) return '';

    return `There are unmerged line items (${parts.join(' and ')}).`;
  })();
  const hasMergeStepWarnings = hasReallocationMismatch || (!allLineItemsArePayappOnly && hasUnmatchedLineItems);

  const handleNext = async () => {
    if (state.subtotalRangeEditActive) {
      toast.error('Finish selecting the subtotal range first — click the checkmark to confirm it before continuing.');
      return;
    }

    if (state.activeStep === 1) {
      const isSuccess = await onSecondStepNext();
      if (!isSuccess) return;

      // Create mapped rows with divisions from PayApp
      const dataWithMappedFieldsAndDivisions = state.tableRows.map((item, rowIndex) => {
        const mapped: Record<string, any> = {};

        // Only map columns that are explicitly assigned in fieldHeadersMapping
        Object.keys(item).forEach(key => {
          if (key !== 'division') {
            // Skip division as we handle it separately
            // Only process if this column is explicitly assigned
            const header = state.fieldHeadersMapping[key];

            // Skip unassigned columns (not in mapping or empty/undefined)
            if (!header || header === '' || header === 'Unassigned') {
              return; // Skip this column
            }

            // Only map if the header matches a valid finalFieldsHeader
            const lineItemField = finalFieldsHeaders.find(f => f.headerName === header)?.field;
            if (lineItemField) {
              mapped[lineItemField] = item[key];
            }
            // If header doesn't match any finalFieldsHeader, skip it (unassigned)
          }
        });

        // Add division object directly
        if (item.division) {
          mapped.division = item.division;
        }

        // Subtotal marker (see Step 2's Select Subtotals / Mark as Subtotal) isn't a mapped
        // column, so it needs the same explicit carryover as division.
        if (item.isSubtotal) {
          mapped.isSubtotal = true;
          mapped.subtotalRange = item.subtotalRange ?? null;
        }

        // Change order marker (see Step 2's Mark as Change Order / Move to Change Orders) isn't a
        // mapped column either — without this, mergePayappIntoProjectDraw never sees it and the
        // ChangeOrder label is never applied to the line item.
        if (item.isChangeOrder) {
          mapped.isChangeOrder = true;
        }

        // Stable id assigned before any exact/fuzzy/no-match resolution runs, so a subtotal's
        // anchor can be traced through the merge to whichever row ends up surviving.
        mapped.__payappSourceId = `payapp-src-${rowIndex}`;

        return mapped;
      });

      // Update finalizedParsedRows with the division-aware data
      setFinalizedParsedRows(dataWithMappedFieldsAndDivisions);

      // Pull subtotal rows out of the line-item stream entirely — they must never be matched
      // against real project line items (a "X Subtotals" description has no business being
      // fuzzy/exact-matched as if it were a budget line item). Each subtotal remembers which
      // real row (by its stable source id) was immediately above it in Step 2's order.
      const pendingSubtotals: { label: string; anchorSourceId: string | null; range: number | null }[] = [];
      let lastRealRowSourceId: string | null = null;
      const realRowsForMerge = dataWithMappedFieldsAndDivisions.filter(row => {
        if (row.isSubtotal) {
          pendingSubtotals.push({
            label: row.description || 'Subtotal',
            anchorSourceId: lastRealRowSourceId,
            range: row.subtotalRange ?? null,
          });
          return false;
        }
        lastRealRowSourceId = row.__payappSourceId;
        return true;
      });

      // Generate updated project draw with division preferences from PayApp
      const coverFieldSubset = {
        LessPreviousFundings: state.coverPageValues['Less Previous Fundings'],
        AdjustmentsbyCDCC: state.coverPageValues['Adjustments by CDCC'],
        RequestedAmount: state.coverPageValues['Requested Amount'],
        EstRetainageReleasedThisPeriod: state.coverPageValues['Est. Retainage Released This Period'],
        applicationNumber: state.coverPageValues['Application Number'],
        PeriodBeginning: state.coverPageValues['Period Beginning'],
        PeriodEnding: state.coverPageValues['Period Ending'],
      };

      // Determine which columns were assigned in step 2 (used for merge / discrepancy logic)
      const assignedPayappColumns = new Set(Object.values(state.fieldHeadersMapping));

      // In second-payapp flow: store the raw (pre-mapped) payapp-2 rows so we can
      // restore step 2 on Back. We must save state.tableRows here (original CSV
      // column-keyed data), NOT dataWithMappedFieldsAndDivisions.  Saving the
      // mapped version would cause the second Next press to try to re-map already-
      // mapped keys through fieldHeadersMapping, finding no matches and producing
      // an empty merge input (so step-3 would not reflect any changes made during
      // the second visit to step 2).
      if (state.isSecondPayappFlow) {
        updateState({ secondPayappTableRows: state.tableRows });
      }

      // For payapp-2 workflow, merge payapp-2 rows into payapp-1 final draw so that
      // "project values" in step 3 are payapp-1 final values.
      // Reset isCreated on all payapp-1 items so payapp-2 can match against them —
      // payapp-1 "payapp-only" items have isCreated:true which would otherwise exclude
      // them from exact-match lookup in mergePayappIntoProjectDraw, causing payapp-2 rows
      // to be treated as brand-new items (showing payapp-2 OB instead of payapp-1 OB).
      const mergeBaseDraw = (() => {
        if (!state.isSecondPayappFlow || !state.payapp1FinalDraw) return state.originalProjectDraw;
        return {
          ...state.payapp1FinalDraw,
          lineItems: (state.payapp1FinalDraw.lineItems || []).map((item: any) => ({
            ...item,
            isCreated: false,
            exitingAndNotMatched: false,
          })),
        };
      })();

      const mergedProjectDraw = mergePayappIntoProjectDraw(realRowsForMerge, mergeBaseDraw, coverFieldSubset, assignedPayappColumns);

      // Check if merge was successful before auto-merging fuzzy matches
      if (!mergedProjectDraw) {
        return; // Error already shown by mergePayappIntoProjectDraw
      }

      // Accumulate system_exact merges (items matched by description in mergePayappIntoProjectDraw)
      const analyticsDrawName = state.projectDraw?.projectDrawSummary?.name ?? '';
      const sessionId = payappSessionIdRef.current;
      (mergedProjectDraw.lineItems ?? [])
        .filter((item: any) => item.matchType === 'exact' && item.isEdited && !item.isCreated && !item.isDeleted)
        .forEach((item: any) => {
          pendingMergeEventsRef.current.push({
            eventName: ANALYTICS_EVENTS.PAYAPP_LINE_ITEM_MERGED,
            properties: {
              project_id: String(state.projectId ?? ''),
              project_label: projectLabel,
              draw_name: analyticsDrawName,
              payapp_session_id: sessionId,
              merge_type: 'system_exact',
              fuzzy_match_type: 'description',
              payapp_description: item.lineItem?.description ?? '',
              payapp_revised_budget: (item.originalBudget ?? 0) + (item.reallocation ?? 0) + (item.changeOrder ?? 0),
              payapp_line_item_id: '',
              project_description: item.lineItem?.description ?? '',
              project_revised_budget: (item.originalBudget ?? 0) + (item.reallocation ?? 0) + (item.changeOrder ?? 0),
              project_line_item_id: item.id ?? '',
            },
          });
        });

      // Collect fuzzy pairs before auto-merge so we can identify which ones get merged
      const mergedLineItems = mergedProjectDraw.lineItems ?? [];
      const fuzzyPairsBefore = mergedLineItems
        .filter((item: any) => item.matchType === 'fuzzy' && item.isCreated && !item.isDeleted)
        .map((payappItem: any) => ({
          payappItem,
          projectItem: mergedLineItems.find((i: any) => i.groupId === payappItem.groupId && i.exitingAndNotMatched),
        }));

      // Automatically merge fuzzy matches that meet auto-merge criteria
      const autoMergedDraw = autoMergeFuzzyMatches(mergedProjectDraw, assignedPayappColumns);

      // Accumulate system_auto merges (pairs whose descriptions matched after normalization)
      fuzzyPairsBefore.forEach(({ payappItem, projectItem }: any) => {
        const isNowDeleted = (autoMergedDraw.lineItems ?? []).some((i: any) => i.id === payappItem.id && i.isDeleted);
        if (isNowDeleted && projectItem) {
          pendingMergeEventsRef.current.push({
            eventName: ANALYTICS_EVENTS.PAYAPP_LINE_ITEM_MERGED,
            properties: {
              project_id: String(state.projectId ?? ''),
              project_label: projectLabel,
              draw_name: analyticsDrawName,
              payapp_session_id: sessionId,
              merge_type: 'system_auto',
              fuzzy_match_type: 'description',
              payapp_description: payappItem.lineItem?.description ?? '',
              payapp_revised_budget: (payappItem.originalBudget ?? 0) + (payappItem.reallocation ?? 0) + (payappItem.changeOrder ?? 0),
              payapp_line_item_id: payappItem.id ?? '',
              project_description: projectItem.lineItem?.description ?? '',
              project_revised_budget: (projectItem.originalBudget ?? 0) + (projectItem.reallocation ?? 0) + (projectItem.changeOrder ?? 0),
              project_line_item_id: projectItem.id ?? '',
              fuzzy_score: payappItem.fuzzyScore,
              budget_match: payappItem.budgetMatch,
            },
          });
        }
      });

      // In payapp-2 workflow, post-process merged line items to apply the 3 special column rules:
      //   previousApplication  = payapp-1 PA  (from payapp1FinalDraw directly)
      //   completeThisPeriod   = CT1 + CT2     (payapp-1 CTP + payapp-2 CTP)
      //   storedThisPeriod     = ST1 + ST2     (payapp-1 STP + payapp-2 STP)
      //
      // We look up payapp-1 values directly from payapp1FinalDraw by item id, rather than
      // relying on item.projectRow / item.payappRow, because mergePayappIntoProjectDraw
      // deletes those when values happen to match (no mismatch), which would otherwise
      // prevent the CT1+CT2 / ST1+ST2 summation.
      const payapp1ItemById: Record<string, any> = {};
      const payapp1CreatedIds = new Set<string>();
      if (state.isSecondPayappFlow && state.payapp1FinalDraw) {
        (state.payapp1FinalDraw.lineItems || []).forEach((p1: any) => {
          if (p1.id) {
            payapp1ItemById[p1.id] = p1;
            if (p1.isCreated === true) payapp1CreatedIds.add(p1.id);
          }
        });
      }

      // Resolve each pending subtotal's anchor against the fully-merged/auto-merged line items.
      // A subtotal's source row resolves to one of:
      //   - an exact match or a brand-new payapp-only row: its own (stable) id
      //   - the payapp-side of a fuzzy pair: __fuzzyProjectSideId, the project row's id — which is
      //     what mergedItem.id becomes once the pair is merged (auto or manual), so this stays
      //     correct whether or not the merge has happened yet
      // Ids are stable across auto-merge/manual-merge in this codebase (mergedItem always keeps
      // `id: projectRow.id`), so this resolution done once here remains valid through later merge
      // actions the user takes in Step 3/4 — no re-anchoring needed inside the merge functions.
      const resolveSubtotalAnchor = (sourceId: string | null, lineItems: any[]): string | null => {
        if (!sourceId) return null;
        const sourceItem = lineItems.find(li => li.__payappSourceId === sourceId);
        if (!sourceItem) return null;
        return sourceItem.__fuzzyProjectSideId || sourceItem.id;
      };

      const resolvedSubtotals: ProjectLineItemSubtotalDTO[] = pendingSubtotals
        .map((pending, idx) => {
          const anchorId = resolveSubtotalAnchor(pending.anchorSourceId, autoMergedDraw.lineItems ?? []);
          if (!anchorId) return null; // no valid row above it (e.g. it was the very first row) — drop it
          const anchorItem = (autoMergedDraw.lineItems ?? []).find((li: any) => li.id === anchorId);
          const subtotal: ProjectLineItemSubtotalDTO = {
            id: `temp-subtotal-${Date.now()}-${idx}`,
            projectDrawId: autoMergedDraw.projectDrawSummary.id,
            anchorDrawLineItemId: anchorId,
            anchorSerialNumber: anchorItem?.serialNumber ?? 0,
            label: pending.label,
            range: pending.range,
            originalBudget: 0,
            changeOrder: 0,
            reallocation: 0,
            revisedBudget: 0,
            previousApplication: 0,
            completeThisPeriod: 0,
            storedThisPeriod: 0,
            cdccApproved: 0,
            completedStoredToDate: 0,
            percentageComplete: 0,
            balanceToComplete: 0,
            retainage: 0,
            quantity: 0,
          };
          return subtotal;
        })
        .filter((s): s is ProjectLineItemSubtotalDTO => s !== null);

      // The payapp's own subtotals fully replace whatever the project already had — rather than
      // trying to match an old subtotal to a new one, mark every pre-existing subtotal (carried
      // through via mergePayappIntoProjectDraw's `{ ...draw, ... }` return, still real ids) as
      // deleted, and let the freshly-resolved ones from this payapp stand in their place. Both
      // arrays get reconciled on the next save (see ProjectWrapper.tsx's saveProject).
      const oldSubtotalsMarkedDeleted = (autoMergedDraw.subtotals ?? []).map(s => ({ ...s, isDeleted: true }));

      const autoMergedDrawWithSubtotals: ProjectLineItemsAndSummary = {
        ...autoMergedDraw,
        subtotals: [...oldSubtotalsMarkedDeleted, ...resolvedSubtotals],
      };

      const updatedProjectDraw = state.isSecondPayappFlow
        ? {
            ...autoMergedDrawWithSubtotals,
            lineItems: (autoMergedDrawWithSubtotals.lineItems || []).map((item: any) => {
              // Items that are the project-side of a fuzzy pair or standalone project-only
              // rows must NEVER get isCreated=true, even if they had isCreated=true in
              // payapp-1. Stamping them isCreated=true would make them look like payapp-only
              // rows (wrong colour, wrong merge button side, broken merge logic).
              const isProjectSide = item.exitingAndNotMatched === true;

              // Pure payapp-2 items (no payapp-1 entry) keep their own flags unchanged.
              const p1Item = payapp1ItemById[item.id];
              if (!p1Item) {
                return item;
              }

              // Project-side items: keep their current flags, only patch CT/ST values.
              if (isProjectSide) {
                const ct1 = p1Item.completeThisPeriod ?? 0;
                const ct2 = item.completeThisPeriod ?? 0;
                const st1 = p1Item.storedThisPeriod ?? 0;
                const st2 = item.storedThisPeriod ?? 0;
                const completeThisPeriod = ct1 + ct2;
                const storedThisPeriod = st1 + st2;
                return {
                  ...item,
                  completeThisPeriod,
                  storedThisPeriod,
                  cdccApproved: completeThisPeriod + storedThisPeriod,
                  // Explicitly keep project-side flags intact — do NOT inherit isCreated
                };
              }

              // Merged / exact-match items: apply CT1+CT2, ST1+ST2 and carry over
              // isCreated from payapp-1 so that lines created when the budget was empty
              // keep isCreated=true instead of being wrongly flagged as updated.
              const pa1 = p1Item.previousApplication ?? 0;
              const ct1 = p1Item.completeThisPeriod ?? 0;
              const ct2 = item.completeThisPeriod ?? 0;
              const st1 = p1Item.storedThisPeriod ?? 0;
              const st2 = item.storedThisPeriod ?? 0;
              const completeThisPeriod = ct1 + ct2;
              const storedThisPeriod = st1 + st2;

              return {
                ...item,
                previousApplication: pa1,
                completeThisPeriod,
                storedThisPeriod,
                cdccApproved: completeThisPeriod + storedThisPeriod,
                isCreated: payapp1CreatedIds.has(item.id) || item.isCreated,
              };
            }),
          }
        : autoMergedDrawWithSubtotals;

      setProjectDraw(updatedProjectDraw);

      // Detect skipped payapp (single-payapp flow only)
      if (!state.isSecondPayappFlow) {
        const { isSkipped, skippedRange } = detectSkippedPayapp(updatedProjectDraw, state.originalProjectDraw, state.coverPageValues);
        if (isSkipped) {
          updateState({ isSkippedPayapp: true, skippedPayappRange: skippedRange });
          const rangeStr = skippedRange
            ? skippedRange.start === skippedRange.end
              ? `[${skippedRange.start}]`
              : `[${skippedRange.start}-${skippedRange.end}]`
            : '';
          const confirmed = await getConfirmation({
            heading: 'Skipped Payapp Detected',
            description: `Payapp appears to have been skipped. Would you like to log payapp ${rangeStr} as "Not inspected by CDCC" and auto-update the report?<br/><br/>You can always change this from the toggle in the footer.`,
            confirmButton: { label: 'Yes, handle it', color: 'primary' },
            cancelButton: { label: 'No', color: 'secondary' },
          });
          updateState({ handleSkippedPayapp: confirmed });
          if (confirmed) {
            const payappLPF = parseCurrency((state.coverPageValues['Less Previous Fundings'] ?? '0').toString());
            const skippedCols = ['previousApplication', 'completeThisPeriod', 'storedThisPeriod'] as const;
            const lineItemsWithPayappValues = updatedProjectDraw.lineItems?.map((item: any) => {
              if (!item.payappRow) return item;
              const updates: Record<string, any> = {};
              for (const field of skippedCols) {
                const v = item.payappRow?.[field];
                if (v !== undefined) updates[field] = v;
              }
              return { ...item, ...updates };
            });
            updateState({ preSkippedDrawLineItems: updatedProjectDraw.lineItems ?? null });
            setProjectDraw({
              ...updatedProjectDraw,
              lineItems: lineItemsWithPayappValues,
              budgetSummary: { ...updatedProjectDraw.budgetSummary, updatedLessPreviousFunding: payappLPF },
            });
          }
          setActiveStep(state.activeStep + 1);
          onStepUrlChange?.(state.activeStep + 1);
          window.scrollTo(0, 0);
          setMiniSidenav(dispatch, true);
          return;
        }
      }
    }
    if (isFinalStep) await onFinalStepConfirm();
    if (!isFinalStep) {
      // On step 3 (Merge), if warnings exist, confirm before continuing
      if (state.activeStep === mergeStepIndex && hasMergeStepWarnings) {
        const reallocationLine = hasReallocationMismatch
          ? reallocationDifference === 0
            ? 'The system has identified unexpected reallocations this month.'
            : `The system has identified ${formatCurrency(reallocationDifference.toString())} in unexpected reallocations this month.`
          : '';
        const unmatchedLine =
          !allLineItemsArePayappOnly && hasUnmatchedLineItems && unmatchedLineItemsDescription ? unmatchedLineItemsDescription : '';
        const bullets = [reallocationLine, unmatchedLine]
          .filter(Boolean)
          .map(t => `<li>${t}</li>`)
          .join('');
        const description = bullets ? `<ul style="margin: 0; padding-left: 20px;">${bullets}</ul>` : '';
        const isConfirmed = await getConfirmation({
          heading: 'Are you sure you want to continue?',
          description,
          confirmButton: {
            label: 'Continue',
            color: 'primary',
          },
          cancelButton: {
            label: 'Cancel',
            color: 'secondary',
          },
        });
        if (!isConfirmed) return;
      }

      // Accumulate fuzzy-matched pairs the user did NOT merge when advancing from step 3.
      // Replace any stale not_merged entries first (user may have gone back and re-advanced).
      if (state.activeStep === mergeStepIndex) {
        pendingMergeEventsRef.current = pendingMergeEventsRef.current.filter(
          e => e.eventName !== ANALYTICS_EVENTS.PAYAPP_LINE_ITEM_NOT_MERGED,
        );
        const notMergedLabel = `${state.projectNumber ?? ''} - ${state.projectName ?? ''}`.trim();
        const notMergedDrawName = state.projectDraw?.projectDrawSummary?.name ?? '';
        const notMergedPayappItems = (state.projectDraw?.lineItems ?? []).filter(
          (item: any) => item.matchType === 'fuzzy' && item.isCreated && !item.isDeleted,
        );
        notMergedPayappItems.forEach((payappItem: any) => {
          const projectItem = (state.projectDraw?.lineItems ?? []).find(
            (i: any) => i.groupId === payappItem.groupId && i.exitingAndNotMatched,
          );
          pendingMergeEventsRef.current.push({
            eventName: ANALYTICS_EVENTS.PAYAPP_LINE_ITEM_NOT_MERGED,
            properties: {
              project_id: String(state.projectId ?? ''),
              project_label: notMergedLabel,
              draw_name: notMergedDrawName,
              payapp_session_id: payappSessionIdRef.current,
              fuzzy_match_type: 'description',
              payapp_description: payappItem.lineItem?.description ?? '',
              payapp_revised_budget: (payappItem.originalBudget ?? 0) + (payappItem.reallocation ?? 0) + (payappItem.changeOrder ?? 0),
              payapp_line_item_id: payappItem.id ?? '',
              project_description: projectItem?.lineItem?.description ?? '',
              project_revised_budget: projectItem
                ? (projectItem.originalBudget ?? 0) + (projectItem.reallocation ?? 0) + (projectItem.changeOrder ?? 0)
                : 0,
              project_line_item_id: projectItem?.id ?? '',
              fuzzy_score: payappItem.fuzzyScore,
              budget_match: payappItem.budgetMatch,
            },
          });
        });
      }

      setActiveStep(state.activeStep + 1);
      onStepUrlChange?.(state.activeStep + 1);
      // Scroll to top when advancing steps (e.g. step 3 → step 4)
      window.scrollTo(0, 0);
      // Collapse sidebar when moving to next step
      setMiniSidenav(dispatch, true);
    }
  };

  const handleBack = async () => {
    if (state.activeStep === 0) return;

    if (state.subtotalRangeEditActive) {
      toast.error('Finish selecting the subtotal range first — click the checkmark to confirm it before continuing.');
      return;
    }

    // Show confirmation when going back from step 3 (Merge); step 3 and 4 share content so we don't prompt on step 4
    if (state.activeStep === mergeStepIndex) {
      const isConfirmed = await getConfirmation({
        heading: 'Are you sure?',
        description: `Going back will lose all changes made in the current step. <br/>
          <br/>
          Are you sure you want to go back?`,
        confirmButton: {
          label: 'Go Back',
          color: 'error',
        },
        cancelButton: {
          label: 'Stay',
          color: 'secondary',
        },
      });

      if (!isConfirmed) return;

      // Going back to table step: discard all accumulated merge events — they'll be
      // re-accumulated when the user advances through the merge step again.
      pendingMergeEventsRef.current = [];

      // In second-payapp flow, restore step-2 table from stored raw payapp-2 rows so the user sees the same payapp-2 data
      if (state.isSecondPayappFlow && state.secondPayappTableRows != null && state.secondPayappTableRows.length > 0) {
        updateState({ tableRows: state.secondPayappTableRows });
      }
    }

    // Going back from review step to merge step: drop stale not_merged entries so they
    // are re-evaluated when the user advances from merge step again.
    if (state.activeStep === mergeStepIndex + 1) {
      pendingMergeEventsRef.current = pendingMergeEventsRef.current.filter(
        e => e.eventName !== ANALYTICS_EVENTS.PAYAPP_LINE_ITEM_NOT_MERGED,
      );
    }

    const nextStep = state.activeStep - 1;
    setActiveStep(nextStep);
    onStepUrlChange?.(nextStep);
    // Collapse sidebar when moving to previous step
    setMiniSidenav(dispatch, true);
  };

  const handleCancel = async () => {
    const isConfirmed = await getConfirmation({
      heading: 'Exit PayApp Workflow?',
      description: `Do you want to exit the PayApp workflow?${
        !state.isManualUpdate
          ? ` <br/><br/>Please save any unsaved changes using the <b>Save Draft</b> button, otherwise they will be discarded.`
          : ''
      }`,
      confirmButton: {
        label: 'Exit',
        color: 'error',
      },
      cancelButton: {
        label: 'Stay',
        color: 'secondary',
      },
    });

    if (isConfirmed) {
      Analytics.track(ANALYTICS_EVENTS.TASK_ABANDONED, {
        task_type: state.isManualUpdate ? 'manual_payapp' : 'payapp',
        project_id: String(state.projectId ?? ''),
        project_label: projectLabel,
        draw_name: state.projectDraw?.projectDrawSummary?.name ?? '',
        payapp_session_id: payappSessionIdRef.current,
      });
      navigate(`${ROUTES.PROJECTS}/${state.projectId}`);
    }
  };

  /** Builds the discrepancy adjustment row appended to Previous Fundings when the LPF differs from what was expected. */
  const buildLPFAdjustmentFunding = (
    existingFundings: ProjectPreviousFundingDTO[],
    receivedByContractor: number,
  ): ProjectPreviousFundingDTO => ({
    id: `temp-${Date.now()}-adjustment`,
    applicationNumber: getNextAdjustmentApplicationNumber(existingFundings),
    applicationDate: undefined,
    receivedByContractor,
    approvedAmount: 0,
    retainage: 0,
    percentRequested: 0,
    percentComplete: 0,
    totalContractAmount: 0,
    displayOrder: existingFundings.length + 1,
    comment: undefined,
    reviewedByCDCC: false,
    isDiscrepancy: true,
  });

  /** Returns the project's "expected" LPF = last month's LPF + last month's Recommended Amount. */
  const computeOriginalLPF = () =>
    (state.originalProjectDraw?.budgetSummary?.lessPreviousFundings ?? 0) +
    (state.originalProjectDraw?.budgetSummary?.totalAmountReccForFunding ?? 0);

  const handleSkippedPayappToggle = (checked: boolean) => {
    const payappLPF = parseCurrency((state.coverPageValues['Less Previous Fundings'] ?? '0').toString());
    const skippedCols = ['previousApplication', 'completeThisPeriod', 'storedThisPeriod'] as const;

    let updatedLineItems: any[];
    let preSkippedDrawLineItems: any[] | null;

    if (checked) {
      // Backup current line items before overwriting with payapp values
      preSkippedDrawLineItems = state.projectDraw?.lineItems ?? null;
      updatedLineItems = (state.projectDraw?.lineItems ?? []).map((item: any) => {
        if (!item.payappRow) return item;
        const updates: Record<string, any> = {};
        for (const field of skippedCols) {
          const v = item.payappRow?.[field];
          if (v !== undefined) updates[field] = v;
        }
        return { ...item, ...updates };
      });
    } else {
      // Restore system values from backup
      updatedLineItems = state.preSkippedDrawLineItems ?? state.projectDraw?.lineItems ?? [];
      preSkippedDrawLineItems = null;
    }

    updateState({ handleSkippedPayapp: checked, preSkippedDrawLineItems });
    setProjectDraw({
      ...state.projectDraw,
      lineItems: updatedLineItems,
      budgetSummary: {
        ...state.projectDraw?.budgetSummary,
        updatedLessPreviousFunding: checked ? payappLPF : computeOriginalLPF(),
      },
    });
  };

  const handleSaveDraft = async () => {
    try {
      setIsSavingDraft(true);
      // Convert cover page values from display labels to backend field names
      const backendCoverPageValues: Record<string, string> = {};
      Object.entries(state.coverPageValues || {}).forEach(([label, value]) => {
        const backendField = Object.keys(backendToLabelMap).find(field => backendToLabelMap[field] === label);
        if (backendField) {
          backendCoverPageValues[backendField] = value as string;
        }
      });

      // Find the most recent PayApp PDF file for this project
      let fileBlobName: string | undefined;
      try {
        const projectFiles = await getProjectFiles(state.projectId);
        const pdfFiles = projectFiles.filter(file => file.fileType === 'application/pdf');
        if (pdfFiles.length > 0) {
          // Sort by creation date and take the first one
          const mostRecentPdf = pdfFiles.sort((a, b) => new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime())[0];
          fileBlobName = mostRecentPdf.blobName;
        }
      } catch (error) {
        console.error('Error fetching project files:', error);
        toast.error('Failed to find PayApp file');
      }

      const dto = {
        projectId: state.projectId,
        projectDrawId: state.payappDrawId,
        data: {
          activeStep: state.activeStep,
          fileBlobName,
          step1: {
            coverPageValues: backendCoverPageValues,
            fileUrl: state.fileUrl || null,
          },
          step2: state.tableRows?.length
            ? {
                fieldHeadersMapping: state.fieldHeadersMapping || {},
                tableRows: state.tableRows || [],
                selectedRowIds: state.selectedRowIds || [],
              }
            : null,
          step3: state.projectDraw
            ? {
                projectDraw: state.projectDraw,
                notes: state.notes || [],
              }
            : null,
        },
      };

      // Check if draft already exists before saving
      let isExistingDraft = false;
      try {
        const existingDraft = await getPayappDraft(state.payappDrawId);
        isExistingDraft = existingDraft !== null && existingDraft !== undefined;
      } catch {
        isExistingDraft = false;
      }

      await savePayappDraft(dto);

      // Add history entry after successful save
      const drawName = state.projectDraw?.projectDrawSummary?.name || 'Unknown Draw';
      try {
        await addProjectHistory(state.projectId, [
          {
            Section: 'draw',
            DrawName: drawName,
            Message: isExistingDraft ? `PayApp draft updated for draw '${drawName}'` : `PayApp draft saved for draw '${drawName}'`,
            Type: isExistingDraft ? 'isUpdated' : 'isCreated',
          },
        ]);
      } catch (error) {
        // Silently fail - history logging shouldn't break draft saving
        console.error('Failed to log payapp draft history:', error);
      }

      Analytics.track(ANALYTICS_EVENTS.PAYAPP_SAVE_DRAFT, {
        project_id: String(state.projectId ?? ''),
        project_label: projectLabel,
        draw_name: state.projectDraw?.projectDrawSummary?.name ?? '',
        payapp_session_id: payappSessionIdRef.current,
        active_step: state.activeStep,
      });
      toast.success('Draft saved');
    } catch (e) {
      toast.error('Failed to save draft');
    } finally {
      setIsSavingDraft(false);
    }
  };

  return (
    <Footer height={HEIGHTS.FOOTER_HEIGHT}>
      <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} flex={1}>
        <Stack direction='row' spacing={2} alignItems='center'>
          <FeedbackButton />
          {state.isSkippedPayapp && (state.activeStep === mergeStepIndex || state.activeStep === mergeStepIndex + 1) && (
            <FormControlLabel
              control={
                <Switch
                  checked={!!state.handleSkippedPayapp}
                  onChange={e => handleSkippedPayappToggle(e.target.checked)}
                  color='primary'
                  size='small'
                />
              }
              label={
                <Typography variant='caption' sx={{ fontWeight: 500 }}>
                  Handle Skipped Payapp
                </Typography>
              }
            />
          )}
        </Stack>
        <Stack direction='row' spacing={2} alignItems='center'>
          <Button variant='outlined' color='error' onClick={handleCancel}>
            Exit
          </Button>
          {!state.isManualUpdate && (
            <Button variant='outlined' color='primary' onClick={handleSaveDraft} loading={isSavingDraft}>
              Save Draft
            </Button>
          )}
          {!state.isManualUpdate && (
            <Button variant='outlined' onClick={handleBack} disabled={state.activeStep === 0}>
              Back
            </Button>
          )}
          {showUploadSecondPayapp && (
            <Tooltip title='Upload a second payapp and merge both into the project draw'>
              <span>
                <Button variant='outlined' color='secondary' onClick={onUploadSecondPayapp} startIcon={<CloudUploadIcon />}>
                  Upload Second Payapp
                </Button>
              </span>
            </Tooltip>
          )}
          <Button
            variant='contained'
            onClick={handleNext}
            disabled={state.activeStep == 1 && (state.fixingHeaders || state.selectedRowIds.length > 0)}
          >
            {isFinalStep ? 'Confirm' : 'Next'}
          </Button>
        </Stack>
      </Stack>
    </Footer>
  );
};

export default PayappProcessFooter;
